import React, { FC } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { UserIcon, UserOptionalIcon } from '@modernloop/shared/icons';
import { IconButton, Tooltip } from '@mui/material';

interface Props {
  onUserIconClick: () => void;
  isAttendeeOptional: boolean;
  dataTestId?: string;
}

/**
 * Per-person optional/required switch, used on interviewer rows and on additional-attendee rows.
 *
 * It is a real button so it is reachable by keyboard and exposes an accessible name. The tooltip
 * uses `describeChild` so it describes the button instead of replacing that name, and the click is
 * stopped from propagating because these rows sit inside clickable containers (the selector opens
 * its popover on click).
 */
const OptionalAttendeeToggle: FC<Props> = ({ onUserIconClick, isAttendeeOptional, dataTestId }) => {
  const { t } = useTranslation();
  const label = isAttendeeOptional
    ? t('hiring-dashboard-schedule:schedule.communications.optionalAttendee.markRequired')
    : t('hiring-dashboard-schedule:schedule.communications.optionalAttendee.markOptional');

  return (
    <Tooltip title={label} describeChild>
      <IconButton
        aria-label={label}
        data-testid={dataTestId}
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onUserIconClick();
        }}
        // The selector trigger opens its popover from a pointer-down handler, and a chip treats a
        // pointer-down on its delete affordance as the start of a delete. Stopping both here keeps
        // this button from doing either.
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        sx={{
          '&:hover': {
            backgroundColor: (theme) => theme.palette.background.contrast,
          },
          padding: (theme) => theme.spacing(0.3),
          borderRadius: '50%',
        }}
      >
        {isAttendeeOptional ? <UserOptionalIcon /> : <UserIcon />}
      </IconButton>
    </Tooltip>
  );
};

export default OptionalAttendeeToggle;
